import { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteCampaignItem } from 'api/campaigns';
import { AuctionItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteItemProps {
    campaignId: number;
    item: AuctionItem;
    onDelete?: () => void;
    children?: ReactNode;
}

const DeleteItem = (props: DeleteItemProps) => {
    const { success, error } = useToast();
    const { item, campaignId, onDelete, children } = props;

    const { mutate: deleteItem } = useMutation({
        mutationFn: () => deleteCampaignItem({ campaignId, itemId: item.id }),
        onSuccess: () => {
            success('Item deleted!');
            onDelete && onDelete();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to delete item');
        },
    });

    return (
        <AlertDialog.Root type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete item</AlertDialog.Title>
                        <AlertDialog.Description>
                            Deleting an item is a permanent action and cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            className={`disabled:cursor-not-allowed disabled:opacity-60`}
                            disabled={!item.can_delete}
                            onClick={() => deleteItem()}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteItemTrigger = AlertDialog.Trigger;

const Root = DeleteItem;
const Trigger = DeleteItemTrigger;

export {
    DeleteItem,
    DeleteItemTrigger,
    //
    Root,
    Trigger,
};
export type { DeleteItemProps };
